import { useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus, RotateCcw } from "lucide-react";
import api, { apiErrorDetail } from "../lib/api";

const inputCls = "w-full bg-[#151821] border border-white/15 px-3 py-2.5 font-mono text-[11px] text-ink placeholder:text-mute focus:outline-none focus:border-acid";

const RETURN_STATUS_COLOR = { requested: "text-[#F59E0B]", approved: "text-[#3B82F6]", refunded: "text-[#22C55E]", rejected: "text-red-400" };

export default function AdminReturns({ returns, restock, products, shiprocketOn, onReload }) {
  const [busyId, setBusyId] = useState(null);
  const [newOrderNum, setNewOrderNum] = useState("");
  const [newReason, setNewReason] = useState("");
  const [addProduct, setAddProduct] = useState("");
  const [addColor, setAddColor] = useState("");
  const [addSize, setAddSize] = useState("");
  const [addQty, setAddQty] = useState(1);

  const selectedProduct = products.find((p) => p._id === addProduct);

  const act = async (rid, path, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusyId(rid);
    try {
      await api.post(path);
      toast.success("Done");
      onReload();
    } catch (e) {
      toast.error(apiErrorDetail(e));
    }
    setBusyId(null);
  };

  const createReturn = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/returns", { order_number: newOrderNum, reason: newReason });
      toast.success(`Return logged for ${data.order_number} — refund if approved ₹${(data.refund_if_approved || 0).toLocaleString("en-IN")}`);
      setNewOrderNum(""); setNewReason("");
      onReload();
    } catch (err) {
      toast.error(apiErrorDetail(err));
    }
  };

  const addRestock = async (e) => {
    e.preventDefault();
    if (!addProduct || !addColor || !addSize) return toast.error("Pick product, colour and size");
    try {
      await api.post("/admin/restock", { product_id: addProduct, color: addColor, size: addSize, qty: Number(addQty) });
      toast.success("Restock saved");
      setAddQty(1);
      onReload();
    } catch (err) {
      toast.error(apiErrorDetail(err));
    }
  };

  const removeRestock = async (rid) => {
    if (!window.confirm("Remove this restock entry?")) return;
    try {
      await api.delete(`/admin/restock/${rid}`);
      toast.success("Restock entry removed");
      onReload();
    } catch (err) {
      toast.error(apiErrorDetail(err));
    }
  };

  const refundPreview = (r) => {
    if (!r.order) return "—";
    return `₹${Math.max(r.order.total - 65, 0).toLocaleString("en-IN")} (${r.order.method.toUpperCase()})`;
  };

  return (
    <div className="mt-8 space-y-10">
      {/* RETURN REQUESTS */}
      <div>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <p className="font-mono text-[11px] tracking-[0.3em] text-volt">RETURN REQUESTS</p>
          <p className="font-mono text-[9px] tracking-widest text-mute">
            SHIPROCKET: {shiprocketOn ? <span className="text-[#22C55E]">CONNECTED</span> : <span className="text-[#F59E0B]">NOT CONNECTED — SHIP RESTOCKS WITH MANUAL TRACKING FOR NOW</span>}
          </p>
        </div>
        {returns.length === 0 ? (
          <div className="border border-white/10 bg-[#0E1015] p-8">
            <p className="font-mono text-[11px] tracking-widest text-mute">NO RETURN REQUESTS YET.</p>
            <p className="font-mono text-[10px] text-mute mt-2 leading-relaxed">
              Customers hit REQUEST RETURN on their order page (within 7 days of delivery). If someone emails or calls instead, log it below.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto border border-white/10">
            <table className="w-full font-mono text-[11px]">
              <thead>
                <tr className="bg-[#0E1015] text-mute tracking-widest text-left">
                  {["ORDER", "CUSTOMER", "REASON", "REFUND IF APPROVED", "STATE", "ACTIONS"].map((h) => (
                    <th key={h} className="px-4 py-3 border-b border-white/10 font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {returns.map((r) => (
                  <tr key={r._id} data-testid={`return-row-${r.order_number}`} className="border-b border-white/[0.06] hover:bg-white/[0.02] align-top">
                    <td className="px-4 py-3 text-acid">
                      {r.order_number}
                      <br /><span className="text-mute text-[9px]">{new Date(r.created_at).toLocaleDateString("en-IN")}</span>
                    </td>
                    <td className="px-4 py-3 text-dim">
                      {r.order ? <>{r.order.customer.first_name}<br /><span className="text-mute text-[9px]">{r.email}</span></> : r.email}
                    </td>
                    <td className="px-4 py-3 text-dim max-w-[240px] whitespace-pre-wrap">{r.reason}</td>
                    <td className="px-4 py-3">{refundPreview(r)}{r.refund_amount ? <><br /><span className="text-[9px] text-mute">refunded {r.refund_amount}</span></> : null}</td>
                    <td className={`px-4 py-3 ${RETURN_STATUS_COLOR[r.status] || "text-dim"}`}>{(r.status || "").toUpperCase()}</td>
                    <td className="px-4 py-3">
                      {r.status === "requested" && (
                        <div className="flex flex-col gap-1.5">
                          <button
                            data-testid={`return-approve-btn-${r.order_number}`} disabled={busyId === r._id}
                            onClick={() => act(r._id, `/admin/returns/${r._id}/approve`,
                              r.order && r.order.method === "prepaid"
                                ? `Refund ₹${Math.max(r.order.total - 65, 0)} to the customer via Razorpay (₹65 return shipping deducted) and restock the tee(s)? Only click AFTER the tee reaches you.`
                                : `Approve this return and restock the tee(s)? COD refund of ₹${r.order ? Math.max(r.order.total - 65, 0) : 0} is sent manually — use MARK REFUND SENT after paying.`)}
                            className="flex items-center gap-1.5 bg-volt text-void px-3 py-1.5 font-bold hover:bg-acid transition-colors disabled:opacity-40"
                          >
                            <RotateCcw size={10} /> APPROVE & {r.order?.method === "prepaid" ? "REFUND" : "RESTOCK"}
                          </button>
                          <button
                            data-testid={`return-reject-btn-${r.order_number}`} disabled={busyId === r._id}
                            onClick={() => act(r._id, `/admin/returns/${r._id}/reject`, "Reject this return? The customer keeps the tee and gets no refund.")}
                            className="border border-white/20 px-3 py-1.5 text-mute hover:border-red-500 hover:text-red-400 transition-colors disabled:opacity-40"
                          >
                            REJECT
                          </button>
                        </div>
                      )}
                      {r.status === "approved" && (
                        <button
                          data-testid={`return-mark-refunded-btn-${r.order_number}`} disabled={busyId === r._id}
                          onClick={() => act(r._id, `/admin/returns/${r._id}/mark-refunded`, "Confirm you have sent the refund manually (UPI/bank)?")}
                          className="bg-[#22C55E] text-void px-3 py-1.5 font-bold hover:opacity-80 transition-opacity disabled:opacity-40"
                        >
                          MARK REFUND SENT
                        </button>
                      )}
                      {r.status !== "requested" && r.status !== "approved" && <span className="text-mute">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <form onSubmit={createReturn} className="border border-white/10 bg-[#0E1015] p-5 mt-4 flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[180px]">
            <label className="block font-mono text-[9px] tracking-[0.25em] text-dim mb-2">LOG A RETURN RECEIVED BY EMAIL / PHONE</label>
            <input data-testid="admin-return-order-input" className={inputCls} placeholder="ORDER NUMBER (RV…)" value={newOrderNum} onChange={(e) => setNewOrderNum(e.target.value.toUpperCase())} required />
          </div>
          <input data-testid="admin-return-reason-input" className={`${inputCls} flex-1 min-w-[180px]`} placeholder="REASON (e.g. customer emailed — size issue)" value={newReason} onChange={(e) => setNewReason(e.target.value)} />
          <button data-testid="admin-return-create-btn" type="submit" className="flex items-center gap-2 border border-acid text-acid font-display font-bold text-xs tracking-widest px-6 py-3 hover:bg-acid hover:text-void transition-colors">
            <Plus size={12} /> LOG RETURN
          </button>
        </form>
      </div>

      {/* RESTOCK INVENTORY */}
      <div>
        <p className="font-mono text-[11px] tracking-[0.3em] text-volt mb-2">RESTOCK INVENTORY — RETURNED TEES READY TO RESHIP</p>
        <p className="font-mono text-[10px] text-mute mb-4 leading-relaxed">
          When a return is approved, the tee lands here automatically. When a new order matches this colour + size, the ORDERS tab shows RESTOCK MATCH —
          ship that unit from your place instead of printing a new one.
        </p>
        <div className="overflow-x-auto border border-white/10">
          <table className="w-full font-mono text-[11px]">
            <thead>
              <tr className="bg-[#0E1015] text-mute tracking-widest text-left">
                {["PRODUCT", "COLOUR", "SIZE", "QTY", "SOURCE", "UPDATED", ""].map((h) => (
                  <th key={h} className="px-4 py-3 border-b border-white/10 font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {restock.map((r) => (
                <tr key={r._id} data-testid={`restock-row-${r._id}`} className="border-b border-white/[0.06] hover:bg-white/[0.02]">
                  <td className="px-4 py-3">{r.title || r.slug || r.product_id}</td>
                  <td className="px-4 py-3 text-dim">{r.color}</td>
                  <td className="px-4 py-3 text-dim">{r.size}</td>
                  <td className={`px-4 py-3 font-bold ${r.qty > 0 ? "text-[#22C55E]" : "text-mute"}`}>{r.qty}</td>
                  <td className="px-4 py-3 text-mute text-[9px]">{r.source_order_number || "—"}</td>
                  <td className="px-4 py-3 text-mute text-[9px]">{r.updated_at ? new Date(r.updated_at).toLocaleDateString("en-IN") : "—"}</td>
                  <td className="px-4 py-3">
                    <button data-testid={`restock-remove-btn-${r._id}`} onClick={() => removeRestock(r._id)} title="Remove entry"
                      className="border border-white/20 px-2.5 py-1.5 text-mute hover:border-red-500 hover:text-red-400 transition-colors">
                      <Trash2 size={10} />
                    </button>
                  </td>
                </tr>
              ))}
              {restock.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-mute">NO RESTOCKED TEES YET — APPROVED RETURNS LAND HERE AUTOMATICALLY.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <form onSubmit={addRestock} className="border border-white/10 bg-[#0E1015] p-5 mt-4 flex flex-wrap gap-3 items-end">
          <div>
            <label className="block font-mono text-[9px] tracking-[0.25em] text-dim mb-2">ADD STOCK MANUALLY (TEES YOU ALREADY HOLD)</label>
            <div className="flex flex-wrap gap-3">
              <select data-testid="restock-product-select" className={inputCls} value={addProduct} onChange={(e) => { setAddProduct(e.target.value); setAddColor(""); setAddSize(""); }}>
                <option value="">PRODUCT…</option>
                {products.map((p) => <option key={p._id} value={p._id}>{p.title}</option>)}
              </select>
              <select data-testid="restock-color-select" className={inputCls} value={addColor} onChange={(e) => setAddColor(e.target.value)} disabled={!selectedProduct}>
                <option value="">COLOUR…</option>
                {(selectedProduct?.colors || []).map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
              </select>
              <select data-testid="restock-size-select" className={inputCls} value={addSize} onChange={(e) => setAddSize(e.target.value)} disabled={!selectedProduct}>
                <option value="">SIZE…</option>
                {(selectedProduct?.sizes || []).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <input data-testid="restock-qty-input" type="number" min={1} max={50} className={`${inputCls} w-24`} value={addQty} onChange={(e) => setAddQty(e.target.value)} />
              <button data-testid="restock-add-btn" type="submit" className="flex items-center gap-2 bg-volt text-void font-display font-bold text-xs tracking-widest px-6 py-3 hover:bg-acid transition-colors">
                <Plus size={12} /> ADD STOCK
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
